import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import * as api from '../api';

const RESOURCE_CATEGORIES = [
  {
    key: 'raw', label: 'Raw', icon: '⛏',
    resources: ['iron_ore','copper_ore','titanium_ore','tungsten_ore','silicon','rare_crystals','quantum_dust','ice','hydrogen_gas','methane_gas','carbon','biomass'],
  },
  {
    key: 'refined', label: 'Refined', icon: '🔩',
    resources: ['iron_plate','copper_plate','titanium_plate','tungsten_plate','silicon_wafer','crystal_core','quantum_substrate','water','refined_gas','polymer_base','bio_gel','carbon_block'],
  },
  {
    key: 'alloys', label: 'Alloys', icon: '⚗',
    resources: ['steel','heavy_alloy','reinforced_alloy','basic_circuit','advanced_circuit','quantum_processor','fuel_cell','high_density_fuel','bio_fuel','industrial_polymer','composite_material'],
  },
  {
    key: 'parts', label: 'Parts', icon: '⚙',
    resources: ['hull_segment','structural_frame','engine_core','thruster','weapon_mount','ai_core','control_unit','sensor_array','power_core','reactor_unit','shield_generator'],
  },
  {
    key: 'modules', label: 'Modules', icon: '🔧',
    resources: ['engine_module','hull_section','energy_system','ai_system'],
  },
  {
    key: 'ships', label: 'Ships', icon: '🚀',
    resources: ['ship_mining_item','ship_freighter_item','ship_capital_freighter_item','ship_cruiser_item','ship_battleship_item'],
  },
];

function fmtResource(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ResourceCategory({ cat, inventory }) {
  const [open, setOpen] = useState(false);
  const fmtNum = (n) => Number(n || 0).toLocaleString();
  const total = cat.resources.reduce((sum, r) => sum + Number(inventory?.[r] || 0), 0);
  const items = cat.resources.filter(r => Number(inventory?.[r] || 0) > 0);

  return (
    <div
      className="res-cat"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span className="res-cat-icon">{cat.icon}</span>
      <span className="res-cat-label">{cat.label}</span>
      <span className="res-cat-total">{fmtNum(total)}</span>
      {open && items.length > 0 && (
        <div className="res-cat-dropdown">
          {items.map(r => (
            <div key={r} className="res-cat-row">
              <span className="res-cat-name">{fmtResource(r)}</span>
              <span className="res-cat-val">{fmtNum(inventory?.[r])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  const { user, inventory, ships, drones, fleetLimits, activeSector, sectors, setUser } = useGame();
  const fmtNum = (n) => Number(n || 0).toLocaleString();
  const [showSettings, setShowSettings] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const fontSize = user?.settings?.font_size || 14;

  // Apply font size to HTML root
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  const handleFontSize = useCallback(async (size) => {
    const clamped = Math.max(12, Math.min(20, Number(size)));
    try {
      await api.updateSettings({ font_size: clamped });
      setUser(prev => ({ ...prev, settings: { ...prev.settings, font_size: clamped } }));
    } catch (err) { console.error('Settings error:', err); }
  }, [setUser]);

  return (
    <header className="hud-header">
      {/* Top bar: Brand + Nav + Settings */}
      <div className="hud-header-top">
        <div className="hud-brand">
          <div className="hud-logo">
            <span className="logo-icon">◆</span>
            <span className="logo-text">GALAXY</span>
          </div>
          <div className="hud-player">
            <span className="player-rank">CMDR</span>
            <span className="player-name">{user?.display_name || user?.username}</span>
          </div>
        </div>

        <button className="hud-mobile-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-label="Toggle navigation">
          <span className="hamburger-icon">{mobileNavOpen ? '✕' : '☰'}</span>
        </button>

        <nav className={`hud-nav ${mobileNavOpen ? 'nav-open' : ''}`}>
          <NavLink to="/sectors" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">◇</span><span className="nav-label">Sectors</span>
          </NavLink>
          <NavLink to="/fleet" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">▲</span><span className="nav-label">Fleet</span>
          </NavLink>
          <NavLink to="/missions" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">◎</span><span className="nav-label">Missions</span>
          </NavLink>
          <NavLink to="/stations" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">⬡</span><span className="nav-label">Stations</span>
          </NavLink>
          <NavLink to="/production" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">⚙</span><span className="nav-label">Production</span>
          </NavLink>
          <NavLink to="/logistics" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">⇄</span><span className="nav-label">Logistics</span>
          </NavLink>
          <NavLink to="/combat" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">⚔</span><span className="nav-label">Combat</span>
          </NavLink>
          <NavLink to="/market" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">◈</span><span className="nav-label">Market</span>
          </NavLink>
          <NavLink to="/inventory" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">▣</span><span className="nav-label">Cargo</span>
          </NavLink>
          <NavLink to="/corporation" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">⛨</span><span className="nav-label">Corp</span>
          </NavLink>
          <NavLink to="/leaderboard" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">★</span><span className="nav-label">Ranks</span>
          </NavLink>
          <NavLink to="/guide" className={({isActive}) => `hud-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
            <span className="nav-icon">📖</span><span className="nav-label">Guide</span>
          </NavLink>
          {user?.is_admin && (
            <NavLink to="/admin" className={({isActive}) => `hud-nav-link admin-nav-link ${isActive ? 'active' : ''}`} onClick={() => setMobileNavOpen(false)}>
              <span className="nav-icon">⚙</span><span className="nav-label">Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="hud-header-controls">
          <button className="hud-settings-btn" onClick={() => setShowSettings(!showSettings)} title="Settings" aria-label="Settings">
            ⚙
          </button>
        </div>
      </div>

      {/* Settings dropdown */}
      {showSettings && (
        <div className="hud-settings-dropdown" onClick={e => e.stopPropagation()}>
          <div className="settings-row">
            <span className="settings-label">Font Size</span>
            <div className="settings-control">
              <button className="settings-btn" onClick={() => handleFontSize(fontSize - 1)} disabled={fontSize <= 12}>−</button>
              <span className="settings-value">{fontSize}px</span>
              <button className="settings-btn" onClick={() => handleFontSize(fontSize + 1)} disabled={fontSize >= 20}>+</button>
            </div>
          </div>
        </div>
      )}

      {/* Info sub-bar: Resources + Fleet + Sector */}
      <div className="hud-info-bar">
        <div className="hud-info-bar-inner">
          {/* Resources */}
          <div className="hud-resources">
            <div className="hud-resource" title="Credits">
              <span className="resource-icon credits-icon">₢</span>
              <span className="resource-val">{fmtNum(inventory?.credits)}</span>
            </div>
            <div className="hud-resource" title="Energy">
              <span className="resource-icon energy-icon">⚡</span>
              <span className="resource-val">{fmtNum(inventory?.energy)}</span>
            </div>
            <div className="res-cat-divider" />
            {RESOURCE_CATEGORIES.map(cat => (
              <ResourceCategory key={cat.key} cat={cat} inventory={inventory} />
            ))}
          </div>

          {/* Fleet pills */}
          {fleetLimits && (
            <div className="hud-fleet-pills">
              <div className="fleet-pill" title="Ships">
                <span className="fleet-pill-icon">🚀</span>
                <span className="fleet-pill-val">{ships?.length || 0}<span className="fleet-pill-cap">/{fleetLimits.maxShips}</span></span>
              </div>
              <div className="fleet-pill" title="Drones">
                <span className="fleet-pill-icon">🛸</span>
                <span className="fleet-pill-val">{drones?.length || 0}<span className="fleet-pill-cap">/{fleetLimits.maxDrones}</span></span>
              </div>
            </div>
          )}

          {/* Active sector */}
          {activeSector && (
            <div className="hud-active-sector">
              <span className="sector-indicator">◇</span>
              <span className="sector-name-label">{sectors?.find(s => s.id === activeSector)?.name || `Sector #${activeSector}`}</span>
            </div>
          )}
        </div>
      </div>

      <div className="hud-scanline" />
    </header>
  );
}

export default Header;
